import React from "react";
import { Modal, Button, Badge, FormInput, FormSelect } from "@/components/ui";
import { 
  RefreshCcw, Tag, Package, Building, 
  Calculator, DollarSign, Search, Hash, 
  Settings, AlertCircle, Percent, Plus, Save
} from "lucide-react";
import { fmt } from "@/lib/formatters";
import { useInventoryModalLogic } from "../hooks/useInventoryModalLogic";

/**
 * InventoryFormModal component
 * Aligned with Projects module design standards.
 * Uses FormField, Input, and Select for consistent typography.
 */
export const InventoryFormModal = ({ 
  isOpen, 
  onClose, 
  activeTab, 
  onSubmit, 
  dynamicSettings, 
  isEditing, 
  inventory = [], 
  onItemSelect,
  initialData = null 
}) => {
  const {
    searchTerm,
    setSearchTerm,
    showDropdown,
    setShowDropdown,
    filteredItems,
    handleMasterSelect,
    register,
    handleSubmit,
    errors,
    watchedValues,
    onFormSubmit
  } = useInventoryModalLogic({ activeTab, initialData, inventory, isOpen, onSubmit, onItemSelect });

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#2b448f] rounded flex items-center justify-center text-white shadow-sm border border-slate-100">
            <Package size={16} />
          </div>
          <span className="text-[14px] font-semibold text-slate-700 tracking-tight">{`${isEditing ? 'Edit' : 'Add'} Inventory Item`}</span>
          {initialData?.isMaster && (
            <Badge variant="secondary" className="text-[10px] uppercase font-medium bg-emerald-50 text-emerald-600 border-emerald-100">
              <RefreshCcw size={10} className="mr-1" /> Master Synced
            </Badge>
          )}
        </div>
      }
      size="3xl"
    >
      <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-6">
        <div className="max-h-[70vh] overflow-y-auto pr-2 no-scrollbar pb-6 flex flex-col gap-8">
          
          {/* Master Lookup */}
          {!isEditing && (
            <div className="p-5 rounded-xl bg-slate-50 border border-slate-100 flex flex-col sm:flex-row items-center gap-4">
               <div className="flex items-center gap-3 shrink-0">
                  <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm text-slate-400 border border-slate-100">
                    <Search size={16} />
                  </div>
                  <div>
                    <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Master lookup</h4>
                  </div>
               </div>
               <div className="flex-1 w-full relative">
                  <FormInput
                    placeholder="Search existing products..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    inputClassName="font-medium text-slate-700"
                  />
                  {showDropdown && filteredItems.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-100 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto no-scrollbar py-1 animate-in fade-in slide-in-from-top-2">
                       {filteredItems.map(item => (
                         <button
                           key={item._id}
                           type="button"
                           className="w-full px-4 py-2.5 text-left hover:bg-slate-50 transition-colors flex items-center justify-between border-b border-slate-50 last:border-0"
                           onClick={() => handleMasterSelect(item)}
                         >
                            <div className="flex flex-col">
                               <span className="text-[12px] font-medium text-slate-700">{item.name}</span>
                               <span className="text-[10px] text-slate-400 font-medium tracking-tight mt-0.5">{item.itemCode || 'GENERAL'}</span>
                            </div>
                            <Badge variant="secondary" className="text-[9px] font-medium uppercase">Select</Badge>
                         </button>
                       ))}
                    </div>
                  )}
               </div>
               {showDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />}
            </div>
          )}

          {/* Section 1: Definition */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-2.5">
               <span className="text-[11px] font-bold text-slate-900 bg-slate-100 w-5 h-5 flex items-center justify-center rounded-full">1</span>
               <h3 className="text-[12px] font-semibold text-slate-400 uppercase tracking-wider">Item Definition</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div className="sm:col-span-2">
                <FormInput
                  label="Item Name"
                  required
                  error={errors?.name?.message || (typeof errors?.name === 'string' ? errors?.name : null)}
                  {...register("name")}
                  placeholder="e.g. Solar Panel 550W"
                />
              </div>
              <FormInput
                label="Item Code"
                error={errors?.itemCode?.message || (typeof errors?.itemCode === 'string' ? errors?.itemCode : null)}
                {...register("itemCode")}
                placeholder="CODE-123"
              />
              <FormInput
                label="Internal SKU"
                {...register("sku")}
                placeholder="SKU-001"
              />
              <FormSelect
                label="Category"
                required
                error={errors?.category?.message || (typeof errors?.category === 'string' ? errors?.category : null)}
                {...register("category")}
              >
                <option value="">Select Category</option>
                {dynamicSettings?.cats?.map(c => {
                  const val = c.title || c.name || c.categoryName || c.label || (typeof c === 'string' ? c : '');
                  return <option key={c._id || val} value={c._id || val}>{val}</option>;
                })}
              </FormSelect>
              <FormSelect
                label="Unit"
                required
                error={errors?.unit?.message || (typeof errors?.unit === 'string' ? errors?.unit : null)}
                {...register("unit")}
              >
                <option value="">Select Unit</option>
                {dynamicSettings?.units?.map(u => {
                  const val = u.title || u.name || u.unitName || u.label || (typeof u === 'string' ? u : '');
                  return <option key={u._id || val} value={u._id || val}>{val}</option>;
                })}
              </FormSelect>
            </div>
          </div>

          {/* Section 2: Financials */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-2.5">
               <span className="text-[11px] font-bold text-white bg-emerald-500 w-5 h-5 flex items-center justify-center rounded-full">2</span>
               <h3 className="text-[12px] font-semibold text-slate-400 uppercase tracking-wider">Stock & Financials</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
               <FormInput label="Opening Quantity" type="number" {...register("availableQty", { valueAsNumber: true })} />
               <FormInput label="Min Stock Level" type="number" {...register("minQty", { valueAsNumber: true })} />
               <FormInput label="Base Amount (₹)" type="number" {...register("baseAmount", { valueAsNumber: true })} />
               <FormInput label="Discount (-)" type="number" {...register("discount", { valueAsNumber: true })} />
               <FormInput label="IGST (%)" type="number" {...register("igst", { valueAsNumber: true })} />
               <FormInput label="CGST + SGST (%)" type="number" {...register("sgst", { valueAsNumber: true })} />
               <FormInput label="Extra Charges (+)" type="number" {...register("chargeAmount", { valueAsNumber: true })} />
               <FormInput label="Rounding (+/-)" type="number" {...register("adjustments", { valueAsNumber: true })} />
               <FormInput label="BV Points" type="number" {...register("bvPoints", { valueAsNumber: true })} />
            </div>

            <div className="p-6 rounded-xl bg-slate-50 border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 mt-2">
               <div className="flex flex-col">
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Final Inventory Value</span>
                  <span className="text-[12px] text-slate-400 font-medium">Inclusive of all taxes and adjustments</span>
               </div>
               <div className="text-2xl font-bold text-slate-800 font-['Poppins']">
                  ₹{fmt(((Number(watchedValues.baseAmount) || 0) - (Number(watchedValues.discount) || 0) + (Number(watchedValues.igst) || 0) + (Number(watchedValues.sgst) || 0) + (Number(watchedValues.cgst) || 0) + (Number(watchedValues.chargeAmount) || 0) + (Number(watchedValues.adjustments) || 0)))}
               </div>
            </div>
          </div>

        </div>

        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="w-full cursor-pointer sm:flex-1 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-md text-[12px] font-medium transition-all duration-300"
          >
            Discard
          </button>
          <button
            type="submit"
            className="w-full cursor-pointer sm:flex-1 py-2.5 bg-[#f97316] hover:bg-[#ea580c] text-white rounded-md text-[12px] font-medium transition-all duration-300 shadow-sm"
          >
            {isEditing ? 'Update Item' : `Complete Entry`}
          </button>
        </div>
      </form>
    </Modal>
  );
};
